import {stateOptions} from './state-province-options'
import useEffectEvent from '../../hooks/use-effect-event'
import useAddressFields from './useAddressFields'
import useAutocomplete from '../../google-maps-api/hooks/useAutocomplete'
import {useCountry} from '../../hooks'

/**
 * Integrates Google Places (New API) autocomplete into the address form.
 *
 * @param {import('react-hook-form').UseFormMethods} form
 *   The object returned from `useForm`.
 * @param {string} [prefix='']
 *   Optional prefix for field names (e.g. '' or 'shippingAddress.').
 *
 * @returns {{
 *   suggestions: any[],
 *   onPlacePrediction: (placePrediction: any) => Promise<void>
 * }}
 */
const PLACE_FIELDS = ['addressComponents', 'displayName', 'formattedAddress']

export default function useAutocompleteAddress(form, prefix = '') {
    const countryCode = useCountry()
    const {postalCode} = useAddressFields({
        form,
        prefix
    })
    const address1Value = form.watch(`${prefix}address1`) || ''
    const {suggestions, resetSession} = useAutocomplete(address1Value)

    const onPlacePrediction = useEffectEvent(async (placePrediction) => {
        if (!placePrediction) return

        const place = placePrediction.toPlace()

        try {
            await place.fetchFields({fields: PLACE_FIELDS})
        } catch {
            // ignore
        }

        if (!place?.addressComponents?.length) return

        const address1 = place.displayName || place.formattedAddress || ''

        let postcode = ''
        let city = ''
        let stateCode = ''

        // Get each component of the address from the place details,
        // and then fill-in the corresponding field on the form.
        // place.addressComponents are google.maps.GeocoderAddressComponent objects
        // which are documented at http://goo.gle/3l5i5Mr

        for (const component of place.addressComponents) {
            const types = component.types || []
            const has = (t) => types.includes(t)

            switch (true) {
                case has('postal_code'):
                case has('postal_code_prefix'):
                    postcode = `${component.longText || ''}${postcode}`
                    break

                case has('postal_code_suffix'):
                    postcode = `${postcode}-${component.longText || ''}`
                    break

                case has('sublocality_level_1'):
                case has('locality'):
                case has('postal_town'):
                    city = component.longText || ''
                    break

                case has('administrative_area_level_1'):
                    stateCode = getStateCode(countryCode, component.shortText)
                    break

                case has('country'):
                    // verify country
                    if (countryCode && component.shortText && component.shortText !== countryCode)
                        return
                    break
            }
        }

        form.setValue(`${prefix}address1`, address1, {shouldValidate: true})
        form.setValue(
            `${prefix}postalCode`,
            postalCode.parseInputValue ? postalCode.parseInputValue(postcode) : postcode,
            {
                shouldValidate: true
            }
        )

        if (form.getValues(`${prefix}stateCode`) != null) {
            form.setValue(`${prefix}stateCode`, stateCode, {shouldValidate: true})
        }

        form.setValue(`${prefix}city`, city, {shouldValidate: true})
        await resetSession()
    })

    return {suggestions, onPlacePrediction}
}

export const getStateCode = (countryCode, shortName) =>
    countryCode === 'JP'
        ? stateOptions.JP.find((elem) => elem.label === shortName)?.value
        : shortName
